import { i18nToast } from "@/utils/i18nToast";
import { getAuthToken } from "@/utils/authToken";
import {
  endSession,
  isAuthEndpoint,
  isSessionProblem,
  refreshSession,
} from "@/utils/sessionRefresh";
import {
  createHttpClient,
  HttpError,
  HttpRequestConfig,
  HttpResponse,
} from "@/lib/http";
import { toast } from "react-hot-toast";
import { handlePaymentRequired } from "@/utils/paymentRequired";
import { isReauthRequiredPayload } from "@/utils/reauthRequired";
import { handleConsentRequired } from "@/utils/consentRequired";

// The app's HTTP client. Built on fetch - see lib/http.ts for why there is no
// axios here any more.
export const api = createHttpClient({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  timeout: 30000, // Increased timeout for development
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: HttpRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    }
    return config;
  },
  (error: unknown) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: HttpResponse) => {
    return response;
  },
  async (error: unknown) => {
    if (!(error instanceof HttpError)) return Promise.reject(error);

    // Handle network errors
    if (!error.response) {
      i18nToast.networkError();
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    switch (status) {
      case 401: {
        // Keycloak access tokens live five minutes. Ask Keycloak - through the
        // backend, using its own refresh_token grant - for a new one and replay
        // the request. No token is minted here and no timer renews anything;
        // this runs only because a request was actually rejected.
        const original = error.config as
          | (HttpRequestConfig & { _sessionRetried?: boolean })
          | undefined;

        // Not every 401 is about the token. Deleting an account re-checks the
        // password and answers 401 when it is wrong; refreshing would not help
        // and signing the user out over a typo would be worse than useless.
        if (!isSessionProblem(data)) break;

        if (
          original &&
          !original._sessionRetried &&
          !isAuthEndpoint(original.url)
        ) {
          original._sessionRetried = true;
          const token = await refreshSession();
          if (token) {
            original.headers = {
              ...original.headers,
              Authorization: `Bearer ${token}`,
            };
            return api.request(original);
          }
        }

        // The refresh failed, so the Keycloak session really is over.
        i18nToast.sessionExpired();
        void endSession();
        break;
      }

      case 402:
        // The route is sold at a higher tier. The dialog makes the offer; a
        // toast reading "Error 402" is what used to happen instead.
        handlePaymentRequired(error);
        break;

      case 403:
        // "Sign in again" is not "access denied": the page that asked offers
        // the way back in, and a toast saying the opposite would sit on top of it.
        if (isReauthRequiredPayload(data)) break;
        // A missing consent is offered in place by ConsentGate.
        if (handleConsentRequired(error)) break;
        i18nToast.accessDenied();
        break;

      case 404: {
        const is404Message =
          (data as { message?: string })?.message || "Ressource nicht gefunden";
        console.warn("404 Error:", is404Message);
        break;
      }

      case 422: {
        // Validation errors - keep detailed error handling
        const validationErrors =
          (data as { details?: Array<{ field: string; message: string }> })
            ?.details || [];
        if (validationErrors.length > 0) {
          // Use dynamic import with .then() instead of await
          import("react-hot-toast").then(({ default: toast }) => {
            validationErrors.forEach((err) => {
              toast.error(`${err.field}: ${err.message}`);
            });
          });
        } else {
          i18nToast.validationError();
        }
        break;
      }

      case 429:
        i18nToast.tooManyRequests();
        break;

      case 500:
        i18nToast.serverError();
        break;

      default: {
        // The uniform error body puts the message under `error`; reading only
        // the top level turned e.g. a refused upload type into "Error 415".
        const body = data as {
          message?: string;
          error?: { message?: string };
        };
        const defaultMessage =
          body?.error?.message || body?.message || `Error ${status}`;
        toast.error(defaultMessage);
        break;
      }
    }

    return Promise.reject(error);
  },
);

// Helper function for API error handling
export const handleApiError = (
  error: Error | HttpError | unknown,
  customMessage?: string,
) => {
  console.error("API Error:", error);

  if (customMessage) {
    toast.error(customMessage);
    return;
  }

  const message =
    (error as HttpError<{ message?: string }>)?.response?.data?.message ||
    (error as Error)?.message ||
    "Ein unbekannter Fehler ist aufgetreten";
  toast.error(message);
};

// HTTP method helpers
export const apiHelpers = {
  get: <T = unknown>(url: string, config?: HttpRequestConfig) =>
    api.get<T>(url, config),
  post: <T = unknown>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig,
  ) => api.post<T>(url, data, config),
  put: <T = unknown>(url: string, data?: unknown, config?: HttpRequestConfig) =>
    api.put<T>(url, data, config),
  patch: <T = unknown>(
    url: string,
    data?: unknown,
    config?: HttpRequestConfig,
  ) => api.patch<T>(url, data, config),
  delete: <T = unknown>(url: string, config?: HttpRequestConfig) =>
    api.delete<T>(url, config),
};

export default api;
